import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Tooltip, Grid, Select } from 'antd';
import {
    BellOutlined,
    UserOutlined,
    LogoutOutlined,
    MenuOutlined,
    WarningOutlined,
    CalendarOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    SunOutlined,
    MoonOutlined,
    DesktopOutlined,
    GlobalOutlined,
    QuestionCircleOutlined,
    ShopOutlined,
    DownOutlined,
} from '@ant-design/icons';

import { getNavConfig } from '../navConfig';

import { AuthContext } from '../../../common/constants';
import { useLayout } from '../LayoutContext';
import { getRoleLabel } from '../../../common/auth/roleLabels';
import { hasPerm } from '../../../common/auth/permissions';
import { useNotifications, useReferentiel, useGlobalAgenceState } from '../../../common/hooks';
import { useAppTheme } from '../../../theme/AppThemeProvider';
import { useT, useLocale } from '../../../common/i18n';
import { agencesService } from '../../../common/services';
import logo from '../../../assets/logo/logo-cap-assurances.JPG';

const MULTI_AGENCE_ROLES = ['superAdmin', 'directeur', 'superviseur'];

// ─── Locale toggle ────────────────────────────────────────────────────────
const LocaleToggle = () => {
    const t = useT();
    const { locale, setLocale } = useLocale();
    const next = locale === 'fr' ? 'en' : 'fr';
    return (
        <Tooltip title={t('nav.locale.title', { locale: locale.toUpperCase(), next: next.toUpperCase() })}>
            <button
                className="navbar-icon-btn"
                onClick={() => setLocale(next)}
                aria-label={t('nav.locale.title', { locale: locale.toUpperCase(), next: next.toUpperCase() })}
            >
                <GlobalOutlined />
                <span className="navbar-icon-btn-text">{locale.toUpperCase()}</span>
            </button>
        </Tooltip>
    );
};

// ─── Theme toggle ─────────────────────────────────────────────────────────
const ThemeToggle = () => {
    const t = useT();
    const { mode, effective, toggle } = useAppTheme();
    const label = t(`nav.theme.${mode}_short`);
    const icon = mode === 'system'
        ? <DesktopOutlined />
        : effective === 'dark' ? <MoonOutlined /> : <SunOutlined />;
    return (
        <Tooltip title={t('nav.theme.title', { mode: label })}>
            <button
                className="navbar-icon-btn"
                onClick={toggle}
                aria-label={t('nav.theme.title', { mode: label })}
            >
                {icon}
            </button>
        </Tooltip>
    );
};

// ─── Sélecteur d'agence global (superAdmin, directeur, superviseur) ──────
// Compact : pilule avec icône + nom court ; popup détaillé au clic.
const AgenceSelector = () => {
    const t = useT();
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const [value, onChange] = useGlobalAgenceState();

    if (!agences || agences.length <= 1) return null;

    const currentLabel = value === 'all'
        ? t('nav.agence.all')
        : (agences.find((a) => a._id === value)?.nom || '—');

    return (
        <Tooltip title={`${t('nav.agence.label')} : ${currentLabel}`}>
            <Select
                value={value}
                onChange={onChange}
                size="middle"
                variant="borderless"
                suffixIcon={<DownOutlined style={{ fontSize: 10 }} />}
                className="navbar-agence-select"
                aria-label={t('nav.agence.label')}
                popupMatchSelectWidth={false}
                placement="bottomRight"
                options={[
                    {
                        value: 'all',
                        label: (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <ShopOutlined /> {t('nav.agence.all')}
                            </span>
                        ),
                    },
                    ...agences.map((a) => ({
                        value: a._id,
                        label: (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <ShopOutlined /> {a.nom}
                            </span>
                        ),
                    })),
                ]}
            />
        </Tooltip>
    );
};

// ─── Lien topbar (lien simple ou module avec entry) ───────────────────────
// Note : avec l'approche two-tier (sub-nav contextuelle), tous les liens
// topbar sont de simples liens. Un clic sur un module navigue vers son
// entry (route principale) ; la sub-nav prend le relais pour les sous-pages.
const NavLink = ({ to, icon, label, matches, exact = false, perm, anyOf }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useContext(AuthContext);

    // Vérification permissions
    if (perm && !hasPerm(currentUser, perm)) return null;
    if (anyOf && !anyOf.some((p) => hasPerm(currentUser, p))) return null;

    let isActive;
    if (exact) {
        isActive = location.pathname === to || location.pathname === `${to}/`;
    } else if (matches && matches.length > 0) {
        isActive = matches.some((p) => location.pathname.startsWith(p));
    } else {
        isActive = location.pathname.startsWith(to);
    }

    return (
        <li role="none">
            <button
                type="button"
                role="menuitem"
                className={`navbar-nav-link ${isActive ? 'is-active' : ''}`}
                onClick={() => navigate(to)}
                aria-current={isActive ? 'page' : undefined}
            >
                {icon}
                <span className="navbar-nav-link-label">{label}</span>
            </button>
        </li>
    );
};

// ─── Permissions agrégées d'un mégamenu (au moins un sous-item visible) ────
const collectMegamenuPerms = (mm) =>
    (mm.sections || [])
        .filter((s) => s.type !== 'actions')
        .flatMap((s) => s.items || [])
        .map((it) => it.perm)
        .filter(Boolean);

// ─── Nav Links horizontaux (two-tier navigation : modules seulement) ───────
const NavLinks = () => {
    const t = useT();
    const { dashboard, megamenus } = getNavConfig(t);

    return (
        <ul className="navbar-nav-links" role="menubar">
            <NavLink
                to={dashboard.path}
                icon={dashboard.icon}
                label={dashboard.label}
                exact={dashboard.exact}
                perm={dashboard.perm}
            />
            {megamenus.map((mm) => (
                <NavLink
                    key={mm.key}
                    to={mm.entry}
                    icon={mm.icon}
                    label={mm.label}
                    matches={mm.matches}
                    anyOf={collectMegamenuPerms(mm)}
                />
            ))}
        </ul>
    );
};

// ─── Notif Row (réutilisé du précédent) ───────────────────────────────────
const NotifRow = ({ icon, label, count, onClick }) => (
    <button className="notif-row" onClick={onClick}>
        <div className="notif-row-icon">{icon}</div>
        <div className="notif-row-content">
            <div className="notif-row-label">{label}</div>
            <div className="notif-row-sub">{count > 0 ? `${count}` : '—'}</div>
        </div>
        <div className={`notif-row-count ${count > 0 ? 'active' : ''}`}>{count}</div>
    </button>
);

// ─── Notifications dropdown ───────────────────────────────────────────────
const NotificationsDropdown = ({ open, onClose, counts, navigateAndClose }) => {
    const t = useT();
    if (!open) return null;
    return (
        <div className="navbar-dropdown notif-dropdown" role="menu">
            <div className="dropdown-header notif-header">
                <div className="dropdown-header-title">{t('notif.center.title')}</div>
                <div className="dropdown-header-badge">{counts.total}</div>
            </div>
            <div className="dropdown-subheader">
                {counts.total > 0 ? t('notif.center.pending') : t('notif.center.up_to_date')}
            </div>
            {counts.total === 0 ? (
                <div className="notif-empty">
                    <CheckCircleOutlined style={{ fontSize: 36, color: '#52c41a' }} />
                    <div className="notif-empty-title">{t('notif.center.empty')}</div>
                    <div className="notif-empty-sub">{t('notif.center.empty_sub')}</div>
                </div>
            ) : (
                <div className="notif-list">
                    <NotifRow
                        icon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                        label={t('notif.echeances')}
                        count={counts.echeances}
                        onClick={() => navigateAndClose('/dashboard/echeances')}
                    />
                    <NotifRow
                        icon={<ExclamationCircleOutlined style={{ color: '#C8102E' }} />}
                        label={t('notif.impayes')}
                        count={counts.impayes}
                        onClick={() => navigateAndClose('/dashboard/impayes')}
                    />
                    <NotifRow
                        icon={<WarningOutlined style={{ color: '#fa8c16' }} />}
                        label={t('notif.sinistres')}
                        count={counts.sinistresEnAttente}
                        onClick={() => navigateAndClose('/dashboard/sinistres')}
                    />
                </div>
            )}
            <div className="dropdown-footer">
                <button onClick={onClose} className="dropdown-footer-btn">
                    {t('notif.center.mark_read')}
                </button>
            </div>
        </div>
    );
};

// ─── User dropdown (enrichi : rôle + agence + Aide) ───────────────────────
const UserDropdown = ({ open, currentUser, agenceName, onLogout, onClose }) => {
    const t = useT();
    if (!open) return null;

    const initials = `${currentUser?.firstName?.[0] || ''}${currentUser?.lastName?.[0] || ''}`.toUpperCase();
    const fullName = currentUser
        ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
        : '';

    return (
        <div className="navbar-dropdown user-dropdown" role="menu">
            <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">
                    {currentUser?.photoUrl ? (
                        <img src={currentUser.photoUrl} alt={fullName} />
                    ) : (
                        initials
                    )}
                </div>
                <div className="user-dropdown-info">
                    <div className="user-dropdown-name">{fullName}</div>
                    <div className="user-dropdown-meta">
                        <span className="user-dropdown-meta-label">{t('nav.user.role_label')} :</span>{' '}
                        <span>{getRoleLabel(currentUser?.role)}</span>
                    </div>
                    {agenceName && (
                        <div className="user-dropdown-meta">
                            <span className="user-dropdown-meta-label">{t('nav.user.agence_label')} :</span>{' '}
                            <span>{agenceName}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="user-dropdown-items">
                <Link to="/dashboard/profile" onClick={onClose} className="user-dropdown-item">
                    <UserOutlined /> <span>{t('nav.my_profile')}</span>
                </Link>
                <button onClick={onLogout} className="user-dropdown-item user-dropdown-item-danger">
                    <LogoutOutlined /> <span>{t('nav.logout')}</span>
                </button>
            </div>
        </div>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────
const Navbar = () => {
    const t = useT();
    const auth = useContext(AuthContext);
    const { currentUser } = auth;
    const { toggleSidebar, isXLUp } = useLayout();
    const navigate = useNavigate();

    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);

    const { counts } = useNotifications({
        onNewActivity: ({ deltaSinistres, deltaImpayes }) => {
            if (deltaSinistres > 0) {
                toast.info(t('notif.sinistres') + ` (+${deltaSinistres})`, {
                    autoClose: 6000,
                    onClick: () => navigate('/dashboard/sinistres'),
                });
            }
            if (deltaImpayes > 0) {
                toast.warning(t('notif.impayes') + ` (+${deltaImpayes})`, {
                    autoClose: 6000,
                    onClick: () => navigate('/dashboard/impayes'),
                });
            }
        },
    });

    const userDropdownRef = useRef(null);
    const notifDropdownRef = useRef(null);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const showAgenceSelector = MULTI_AGENCE_ROLES.includes(currentUser?.role);

    // Resolve agence name pour l'afficher dans le dropdown user
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const userAgenceName = (() => {
        if (!currentUser?.agence) return null;
        if (typeof currentUser.agence === 'object') return currentUser.agence.nom;
        const found = (agences || []).find((a) => a._id === currentUser.agence);
        return found?.nom || null;
    })();

    // Fermeture des dropdowns au clic extérieur
    useEffect(() => {
        const handler = (e) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
                setShowUserDropdown(false);
            }
            if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) {
                setShowNotifDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = (e) => {
        e?.preventDefault();
        setShowUserDropdown(false);
        toast.success(t('auth.logout.success'), { autoClose: 1500 });
        setTimeout(() => auth.onLogout(), 800);
    };

    const markNotificationsRead = () => {
        try { localStorage.setItem('notif_last_read', String(Date.now())); } catch { /* */ }
    };

    const navigateAndClose = (path) => {
        markNotificationsRead();
        setShowNotifDropdown(false);
        navigate(path);
    };

    const initials = `${currentUser?.firstName?.[0] || ''}${currentUser?.lastName?.[0] || ''}`.toUpperCase();

    return (
        <nav className="layout-navbar" id="layout-navbar" aria-label="Application navigation">
            {/* Gauche : burger (mobile) + logo + nav links horizontaux (desktop) */}
            <div className="navbar-left">
                {!isXLUp && (
                    <button
                        className="navbar-icon-btn navbar-burger"
                        onClick={toggleSidebar}
                        aria-label={t('nav.open_menu')}
                    >
                        <MenuOutlined />
                    </button>
                )}
                <Link to="/dashboard" className="navbar-brand" aria-label="CAP Assurances">
                    <img src={logo} alt="CAP Assurances" className="navbar-brand-logo" />
                </Link>
                {isXLUp && <NavLinks />}
            </div>

            {/* Droite : actions */}
            <div className="navbar-right">
                {!isMobile && (
                    showAgenceSelector
                        ? <AgenceSelector />
                        : userAgenceName && (
                            <div
                                className="navbar-agence-static"
                                title={`${t('nav.agence.label')} : ${userAgenceName}`}
                            >
                                <ShopOutlined />
                                <span>{userAgenceName}</span>
                            </div>
                        )
                )}

                {!isMobile && <LocaleToggle />}
                <ThemeToggle />

                {/* Centre d'aide global */}
                <Tooltip title={t('nav.help')}>
                    <button
                        className="navbar-icon-btn navbar-help-btn"
                        onClick={() => window.dispatchEvent(new CustomEvent('cap:open-helpcenter'))}
                        aria-label={t('nav.help')}
                    >
                        <QuestionCircleOutlined />
                        {!isMobile && <span className="navbar-icon-btn-text">Aide</span>}
                    </button>
                </Tooltip>

                {/* Notifications */}
                <div className="navbar-dropdown-wrapper" ref={notifDropdownRef}>
                    <Badge
                        count={counts.total}
                        overflowCount={99}
                        offset={[-4, 4]}
                        style={{ backgroundColor: counts.total > 0 ? '#C8102E' : 'var(--cap-text-muted)' }}
                    >
                        <button
                            className="navbar-icon-btn"
                            onClick={() => setShowNotifDropdown((v) => !v)}
                            aria-label={`${t('notif.center.title')} (${counts.total})`}
                            aria-expanded={showNotifDropdown}
                        >
                            <BellOutlined />
                        </button>
                    </Badge>
                    <NotificationsDropdown
                        open={showNotifDropdown}
                        onClose={() => { markNotificationsRead(); setShowNotifDropdown(false); }}
                        counts={counts}
                        navigateAndClose={navigateAndClose}
                    />
                </div>

                {/* User avatar + dropdown */}
                <div className="navbar-dropdown-wrapper" ref={userDropdownRef}>
                    <button
                        className="navbar-user-trigger"
                        onClick={() => setShowUserDropdown((v) => !v)}
                        aria-label={t('nav.my_profile')}
                        aria-expanded={showUserDropdown}
                    >
                        <div className="navbar-user-avatar">
                            {currentUser?.photoUrl ? (
                                <img src={currentUser.photoUrl} alt="" />
                            ) : initials}
                        </div>
                    </button>
                    <UserDropdown
                        open={showUserDropdown}
                        currentUser={currentUser}
                        agenceName={userAgenceName}
                        onLogout={handleLogout}
                        onClose={() => setShowUserDropdown(false)}
                    />
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
